import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useSearchParams } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { useDispatch } from 'react-redux';

// Utils
import {
  LASTSYNCTRANSLATIONS,
  getPreviousRoute,
  getStringStorage,
  isUserAuthenticated
} from '@services/storage';
import { RoutesConstants } from './routesConstants';
import { hasValueString } from '../services/utilsString';

// Components
import LayoutMain from '../components/layouts/layoutMain';
import LoadingComponent from '../components/shared/loading/loadingComponent';

// Api's
import { accountApi, companyPreferenceApi } from '../api';

// Redux
import { useUserReducer, userSetCurrent } from '../store/user';
import { saveTranslates, useTranslateReducer } from '../store/translate';
import { getMenu, useMenuReducer } from '../store/menu';
import { signalRConnect } from '../store/signalR';

// Models
import { Result } from '../models/common/resultResponse';
import { UserResponse } from '../models/response';
import { useTranslateHook } from '../hooks/translateHook';
import { setVisualIdentity } from '../store/visualIdentity';
import { isNullUndefined } from '../services/utils';

function PrivateRoute() {
  const dispatch = useDispatch();
  const menuReducer = useMenuReducer();
  const userReducer = useUserReducer();
  const { translates: translatesReducer } = useTranslateReducer();
  const { getTranslates, isLoadingTranslates } = useTranslateHook({
    fillTranslateLocal: false
  });
  const [isLoadingAccountDetail, setIsLoadingAccountDetail] = useState(true);


  async function loadAccountDetails() {
    try {
      const [responseAccount, responseCompanyPreference] = await Promise.all(
        [
          accountApi.getUserLoggedApi(),
          companyPreferenceApi.GetCompanyPreferencesByLoggedUser()
        ]
      )
      dispatch(userSetCurrent(responseAccount.data.result));
      if (!isNullUndefined(responseCompanyPreference.data.result)) {
        dispatch(setVisualIdentity({
          headerColor: responseCompanyPreference.data.result.headerColor,
          logoUrl: responseCompanyPreference.data.result.logoUrl,
          primaryColor: responseCompanyPreference.data.result.primaryColor
        }))
      }
      getApisByUserLogged(responseAccount.data.result);
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingAccountDetail(false)
    }


  }

  useEffect(() => {
    if (isUserAuthenticated()) {
      loadAccountDetails();
    }
  }, []);

  function getApisByUserLogged(userData: UserResponse) {
    if (userData.idCulture) {
      (async () => {
        const idCultureUserLogged = userData.idCulture;

        // Get translates
        let lastSyncTranslations: string | null = null;
        const lastSyncTranslationsStoraged = getStringStorage(
          `${LASTSYNCTRANSLATIONS}_${idCultureUserLogged}`
        );

        if (hasValueString(lastSyncTranslationsStoraged)) {
          lastSyncTranslations = lastSyncTranslationsStoraged;
        }

        const responseApi = await getTranslates({
          culture: idCultureUserLogged,
          lastSyncTranslations
        });

        if (responseApi.data.result) {
          // Save translates
          dispatch(
            saveTranslates(
              responseApi.data.result,
              idCultureUserLogged,
              false,
              []
            )
          );

          // Get menu
          dispatch(getMenu());

          // Connect signalR
          dispatch(signalRConnect());
        }
      })();
    }
  }



  if (isUserAuthenticated()) {
    // Constants
    const previousRoute = getPreviousRoute();

    const hasPendingTerms =
      (userReducer.currentUser?.pendingAcceptanceTerms?.length ?? 0) > 0;

    const isEmptyTranslates =
      translatesReducer.data === null || translatesReducer.data.length === 0;

    if (
      isLoadingTranslates ||
      isEmptyTranslates ||
      isLoadingAccountDetail ||
      menuReducer.menu.isLoading
    )
      return <LoadingComponent />;

    if (previousRoute) return <Navigate to={previousRoute} />;

    return <LayoutMain>{hasPendingTerms ? <></> : <Outlet />}</LayoutMain>;
  }

  return <Navigate to={RoutesConstants.login} />;
}

export { PrivateRoute };
